import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { 
  TrendingUp, 
  Brain, 
  BarChart3, 
  Target, 
  AlertTriangle, 
  Zap, 
  ShoppingCart, 
  Calendar, 
  ArrowRight,
  CheckCircle,
  XCircle,
  Eye,
  Settings,
  Database,
  Workflow,
  PieChart,
  Clock,
  DollarSign,
  Package,
  Activity,
  Shield
} from "lucide-react";

export function DemandForecasting() {
  const keyObjectives = [
    {
      icon: Target,
      title: "Predict what to stock, when, and how much",
      description: "AI-driven predictions for optimal inventory levels"
    },
    {
      icon: Workflow,
      title: "Align procurement and promotion planning",
      description: "Synchronized planning across all business functions"
    },
    {
      icon: DollarSign,
      title: "Improve working capital efficiency",
      description: "Optimize cash flow through smarter inventory management"
    },
    {
      icon: AlertTriangle,
      title: "Enable exception-based planning with AI alerts",
      description: "Proactive notifications for demand anomalies and opportunities"
    }
  ];

  const todaysChallenges = [
    {
      icon: XCircle,
      title: "Inventory bloating due to over-ordering",
      description: "Excess stock tying up valuable working capital"
    },
    {
      icon: XCircle,
      title: "Frequent stockouts damaging revenue and trust",
      description: "Lost sales and customer dissatisfaction"
    },
    {
      icon: XCircle,
      title: "Promotions running without impact forecasting",
      description: "Marketing spend without demand visibility"
    },
    {
      icon: XCircle,
      title: "Procurement operating without visibility into demand shifts",
      description: "Reactive purchasing leading to inefficiencies"
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: "Reduce waste",
      description: "Minimize overstock and obsolete inventory"
    },
    {
      icon: CheckCircle,
      title: "Increase sales from availability",
      description: "Improve service levels and customer satisfaction"
    },
    {
      icon: CheckCircle,
      title: "Optimize working capital",
      description: "Free up cash flow for strategic investments"
    },
    {
      icon: CheckCircle,
      title: "React faster to real-time market inputs",
      description: "Respond quickly to changing market conditions"
    }
  ];

  const aiCapabilities = [
    {
      capability: "AI/ML Forecasting",
      description: "Predict demand across SKUs, regions, and time windows using trend, seasonality, and external signals",
      icon: Brain
    },
    {
      capability: "Inventory Sensitivity Models",
      description: "Understand impact of safety stock, lead time, and demand variance",
      icon: BarChart3
    },
    {
      capability: "Scenario Simulation",
      description: "Run \"What If\" simulations on promotions, pricing, or vendor changes",
      icon: PieChart
    },
    {
      capability: "Smart Alerts",
      description: "Get notified of anomalies or gaps in demand vs. stock",
      icon: AlertTriangle
    },
    {
      capability: "Promotion-Aware Forecasting",
      description: "Automatically adjust forecasts for active or planned schemes",
      icon: Target
    },
    {
      capability: "ERP Integration",
      description: "Push forecasts directly to procurement/planning systems",
      icon: Database
    }
  ];

  const challengesSolved = [
    {
      challenge: "Inaccurate Forecasts Based on Gut Feel",
      solution: "AI learns from historical + real-time data",
      result: "Forecast accuracy increases with time",
      color: "bg-primary/10 text-primary"
    },
    {
      challenge: "Stockouts During Key Promotions",
      solution: "System detects uplift opportunities from active schemes",
      result: "Suggests buffer inventory or adjusted PO timelines",
      color: "bg-secondary/10 text-secondary"
    },
    {
      challenge: "Overstock on Slow-Moving SKUs",
      solution: "Forecasts at SKU level by region or channel",
      result: "Provides actionable SKU rationalization inputs",
      color: "bg-accent/10 text-accent"
    }
  ];

  const adaptiveFeatures = [
    {
      icon: Activity,
      title: "Forecasts auto-adjust in real time",
      description: "Dynamic updates based on latest market signals"
    },
    {
      icon: Eye,
      title: "Human + AI inputs are combined for higher accuracy",
      description: "Best of both worlds: AI intelligence with human expertise"
    },
    {
      icon: Shield,
      title: "Helps prevent overstocking or stockouts tied to business events",
      description: "Event-aware forecasting for better planning"
    },
    {
      icon: Settings,
      title: "All logic is transparent and editable",
      description: "Full visibility and control over forecasting algorithms"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Solution Overview */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <Badge variant="secondary" className="mb-6">
                <Brain className="mr-1 h-3 w-3" />
                What It Is
              </Badge>
              
              <p className="mb-8 text-lg text-muted-foreground lg:text-xl leading-relaxed">
                Demand Forecasting is an AI-driven solution within the NexAI suite that empowers retail, distribution, and procurement teams to make accurate, adaptive demand plans. It's built for fast-moving, high-SKU environments with changing market dynamics.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="mb-8 text-2xl lg:text-3xl text-center font-bold">Key Objectives</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {keyObjectives.map((objective, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                          <objective.icon className="h-5 w-5 text-white" />
                        </div>
                        <CardTitle className="text-lg">{objective.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{objective.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Why It Matters</h2>
              <p className="text-lg text-muted-foreground">
                Transform your inventory management from reactive to predictive
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Today's Challenges */}
              <div>
                <h3 className="mb-6 text-2xl">Today's Challenges</h3>
                <div className="space-y-4">
                  {todaysChallenges.map((challenge, index) => (
                    <Card key={index} className="border-destructive/20 bg-destructive/5">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <challenge.icon className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="mb-1">{challenge.title}</h4>
                            <p className="text-sm text-muted-foreground">{challenge.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* With Demand Forecasting */}
              <div>
                <h3 className="mb-6 text-2xl">With Demand Forecasting, You Can:</h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <Card key={index} className="border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <benefit.icon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="mb-1 text-green-800">{benefit.title}</h4>
                            <p className="text-sm text-green-700">{benefit.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities & Features */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">AI Capabilities & Features</h2>
              <p className="text-lg text-muted-foreground">
                Advanced AI-powered features that transform your demand planning
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {aiCapabilities.map((capability, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: '#819A40' }}>
                        <capability.icon className="h-5 w-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">{capability.capability}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{capability.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Challenges Solved */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F6FBEA' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Common Challenges Solved</h2>
              <p className="text-lg text-muted-foreground">
                See how AI transforms traditional forecasting problems into opportunities
              </p>
            </div>

            <div className="space-y-8">
              {challengesSolved.map((item, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                      <div>
                        <Badge className={`mb-3 ${item.color}`}>
                          Challenge
                        </Badge>
                        <h3 className="text-lg">{item.challenge}</h3>
                      </div>
                      
                      <div className="flex justify-center">
                        <ArrowRight className="h-8 w-8 text-muted-foreground" />
                      </div>
                      
                      <div>
                        <Badge variant="secondary" className="mb-3">
                          AI Solution
                        </Badge>
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">→ {item.solution}</p>
                          <p className="text-sm text-muted-foreground">→ {item.result}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Adaptive Forecasts */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Adaptive Forecasts with Contextual Intelligence</h2>
              <p className="text-lg text-muted-foreground">
                Smart forecasting that evolves with your business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {adaptiveFeatures.map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10">
                    <feature.icon className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="mb-3 text-xl">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              Ready to Transform Your Demand Planning?
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Reduce stockouts, optimize inventory, and respond to market shifts faster than ever with AI-powered demand forecasting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                Request a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <Brain className="mr-2 h-4 w-4" />
                Explore AI Capabilities
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}